import { Editor, Node, Path, Point, Range } from 'domains/reporter/RichTextEditor/core';
import { Element } from 'slate';
import type { NodeEntry } from 'slate';

import { BRACKET_TYPES } from '../constants';
import { createLineBreak } from '../plugins/lineBreak/utils';
import { PARAGRAPH_PLUGIN_ID } from '../plugins/paragraph/types';
import { isEntireFieldSelected } from './isEntireFieldSelected';

export const getSelectedBracketEntry = (editor: Editor): NodeEntry | null => {
  if (editor.selection == null) {
    return null;
  }

  const bracketEntryAtSelection =
    Editor.above(editor, {
      at: editor.selection,
      match: (node) => Element.isElement(node) && BRACKET_TYPES.includes(node.type),
      mode: 'lowest',
    }) ?? null;
  if (bracketEntryAtSelection != null) {
    return bracketEntryAtSelection;
  }

  if (Range.isCollapsed(editor.selection)) {
    return null;
  }

  const selectedBracketNodes = Array.from(
    Editor.nodes(editor, {
      at: editor.selection,
      match: (node) => Element.isElement(node) && BRACKET_TYPES.includes(node.type),
      mode: 'lowest',
    })
  );

  if (selectedBracketNodes.length === 1) {
    const bracketEntry = selectedBracketNodes[0] as NodeEntry;
    const [, bracketPath] = bracketEntry;
    if (isEntireFieldSelected(editor, bracketPath, editor.selection)) {
      return bracketEntry;
    }
  }

  return null;
};

export const isSelectionInEmptyBracket = (editor: Editor): boolean => {
  if (editor.selection == null) {
    return false;
  }

  const bracketEntry = getSelectedBracketEntry(editor);
  if (bracketEntry == null) {
    return false;
  }

  const [bracketNode] = bracketEntry;
  return Node.string(bracketNode).trim() === '';
};

export const isEntireBracketContentSelected = (editor: Editor, bracketPath: Path): boolean => {
  if (editor.selection == null || Range.isCollapsed(editor.selection)) {
    return false;
  }
  const [start, end] = Range.edges(editor.selection);
  return (
    Point.equals(start, Editor.start(editor, bracketPath)) &&
    Point.equals(end, Editor.end(editor, bracketPath))
  );
};

export const shouldReplaceBracketContentOnPaste = (editor: Editor, bracketPath: Path): boolean => {
  return (
    isEntireFieldSelected(editor, bracketPath, editor.selection) ||
    isEntireBracketContentSelected(editor, bracketPath)
  );
};

type FlattenFragmentForContentOnlyPasteOptions = Readonly<{
  insertLineBreakBetweenListItems?: boolean;
}>;

export const flattenFragmentForContentOnlyPaste = (
  nodes: Array<Node>,
  options: FlattenFragmentForContentOnlyPasteOptions = {}
): Array<Node> => {
  const { insertLineBreakBetweenListItems = true } = options;

  return nodes.flatMap((node) => {
    if (!Element.isElement(node)) {
      return [node];
    }

    if (BRACKET_TYPES.includes(node.type) && node.children != null) {
      return flattenFragmentForContentOnlyPaste(node.children, options);
    }

    if (node.type === PARAGRAPH_PLUGIN_ID && Array.isArray(node.children)) {
      return flattenFragmentForContentOnlyPaste(node.children, options);
    }

    if (node.type === 'list' && Array.isArray(node.children)) {
      const flattenedListChildren: Array<Node> = [];
      node.children.forEach((child, index) => {
        if (insertLineBreakBetweenListItems && index > 0) {
          flattenedListChildren.push(createLineBreak());
        }

        if (Element.isElement(child) && child.type === 'list') {
          flattenedListChildren.push(...flattenFragmentForContentOnlyPaste([child], options));
          return;
        }

        flattenedListChildren.push(...flattenFragmentForContentOnlyPaste([child], options));
      });

      return flattenedListChildren;
    }

    return [node];
  });
};
